import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "./ServicePages.css";
import hero from "./assets/GBPManagement/google-business-profile-management-services-hero.png";
import reviews from "./assets/GBPManagement/google-business-profile-review-generation.png";
import localPack from "./assets/GBPManagement/google-maps-local-pack-ranking.png";
import dashboard from "./assets/GBPManagement/gbp-ongoing-monitoring-dashboard.png";
import fiveStep from "./assets/GBPManagement/google-business-profile-five-step-checklist.png";
import phone from "./assets/GBPManagement/attoli-free-gbp-call.png";
import Footer from "./Footer";

const GBPManagement = () => {
  return (
    <div className="page-wrapper">
      <Helmet>
        <meta
          name="description"
          content="Attoli offers Google Business Profile management services for accounting firms. We set up, optimize, and manage your listing so you show up on Google Maps."
        />
        <title>
          Google Business Profile Management Services For Accountants | Attoli
        </title>
        <link
          rel="canonical"
          href="https://attoli.com/services/google-business-profile-management-services-for-accountants"
        />
      </Helmet>

      <div className="servicePage-container">
        <section className="servicePage-section hero-section">
          <div className="servicePage-content">
            <div className="servicePage-left">
              <h1 className="servicePage-title">
                Google Business Profile Management Services For{" "}
                <span className="hero-accent">Accountants</span>
              </h1>
              <p className="servicePage-description">
                Our Google Business Profile management service keeps your
                listing accurate, active, and easy to find. A strong profile
                helps new clients find your firm on Google Maps before they ever
                visit your website.
              </p>

              <ul className="trust-badges">
                <li>No lock in contracts</li>
                <li>Built only for accounting and bookkeeping firms</li>
                <li>Free 20 minute call before you commit to anything</li>
              </ul>

              <div className="cta-block">
                <Link to="/contact" className="cta-button">
                  Book A Free Profile Review
                </Link>
              </div>
            </div>

            <div className="servicePage-right">
              <img
                src={hero}
                alt="Google Maps pin with a checkmark badge representing a verified and managed Google Business Profile"
                className="servicePage-image"
              />
            </div>
          </div>
        </section>

        <section className="servicePage-section pain-section">
          <div className="section-inner">
            <h2 className="section-title">
              Why An Unmanaged Google Business Profile Is Costing You Clients
            </h2>
            <p className="body-copy">
              Most people search for an accountant on their phone. They type in
              a service and their city, then look at the map that shows up
              first. If your profile is missing information or has old hours,
              they move on to the next firm on the list.
            </p>
            <p className="body-copy">
              A weak profile also means fewer reviews. Most new clients read
              reviews before they call. No reviews or old reviews make your firm
              look less trusted, even if your work is great.
            </p>
            <p className="body-copy">
              Most accounting firms set up their profile once and never look at
              it again. It sits outdated while other firms in your area keep
              theirs fresh and active.
            </p>
            <p className="body-copy body-copy-emphasis">
              Your Google Business Profile is often the first thing a client
              sees. It should look as trustworthy as your firm actually is.
            </p>
          </div>
        </section>

        <section className="servicePage-section value-section">
          <div className="section-inner">
            <h2 className="section-title">
              What Our Google Business Profile Management Service Includes
            </h2>
            <p className="body-copy">
              Attoli handles the setup and the upkeep so your profile stays
              accurate and active all year. Here is what that looks like in
              practice.
            </p>

            <div className="value-grid">
              <div className="value-card">
                <h3>Listing Setup And Verification</h3>
                <p>
                  We create or claim your profile and get it verified with
                  Google. This step alone stops other people from editing your
                  listing without your approval.
                </p>
              </div>
              <div className="value-card">
                <h3>Review Generation And Management</h3>
                <p>
                  We help you collect real reviews from happy clients and
                  respond to every review that comes in. More reviews build more
                  trust with new visitors.
                </p>
              </div>
              <div className="value-card">
                <h3>NAP Consistency Checks</h3>
                <p>
                  We make sure your name, address, and phone number match across
                  your website and other directories. Mismatched information can
                  hurt how Google ranks your listing.
                </p>
              </div>
              <div className="value-card">
                <h3>Photo And Post Updates</h3>
                <p>
                  We add fresh photos and short posts to your profile on a
                  regular basis. An active profile signals to Google that your
                  business is real and open.
                </p>
              </div>
              <div className="value-card">
                <h3>Category And Service Accuracy</h3>
                <p>
                  We review your business category and listed services so the
                  right clients find you for the right reasons.
                </p>
              </div>
              <div className="value-card">
                <h3>Ongoing Performance Reporting</h3>
                <p>
                  We check your profile regularly instead of setting it up once
                  and walking away. You get a simple report showing what changed
                  and why.
                </p>
              </div>
            </div>

            <img
              src={reviews}
              alt="Row of five star review icons growing taller to represent building more client reviews over time"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section keyword-section">
          <div className="section-inner">
            <h2 className="section-title">
              How Google Business Profile Management Affects Your Google Maps
              Ranking
            </h2>
            <p className="body-copy">
              Google looks at more than just your website when it decides who
              shows up in the local map pack. It also looks at your profile
              activity, your reviews, and how closely your listed information
              matches what is on your site.
            </p>

            <img
              src={localPack}
              alt="Google Maps local pack showing three business pins with one highlighted to represent ranking in the map results"
              className="inline-image"
            />

            <p className="body-copy">
              We manage each of these pieces together instead of treating your
              profile as a one time task. Small, steady updates tend to beat one
              big change made once a year.
            </p>
          </div>
        </section>

        <section className="servicePage-section keyword-section-v2">
          <div className="section-inner">
            <h2 className="section-title">
              Why Ongoing Management Works Better Than A One Time Optimization
            </h2>
            <p className="body-copy">
              A one time optimization can clean up your profile, but it does not
              keep it that way. Hours change. Staff change. New reviews come in
              and need a response. Without regular attention, a freshly
              optimized profile can slip back to where it started.
            </p>

            <img
              src={dashboard}
              alt="Simple dashboard gauge staying steady in the green zone over time representing ongoing Google Business Profile monitoring"
              className="inline-image"
            />

            <p className="body-copy">
              We treat your Google Business Profile the same way we treat your
              website. Checked regularly instead of fixed once and forgotten.
            </p>
          </div>
        </section>

        <section className="servicePage-section process-section">
          <div className="section-inner">
            <h2 className="section-title">
              How Our Google Business Profile Management Process Works
            </h2>

            <img
              src={fiveStep}
              alt="Five step checklist for Google Business Profile management covering audit setup reviews updates and reporting"
              className="inline-image"
            />

            <ol className="ledger-list">
              <li>
                <span className="ledger-number"></span>
                <div>
                  <h3>Profile Audit</h3>
                  <p>
                    We check your current listing for missing or incorrect
                    information and see how it compares to other firms nearby.
                  </p>
                </div>
              </li>
              <li>
                <span className="ledger-number"></span>
                <div>
                  <h3>Setup And Verification</h3>
                  <p>
                    We claim or verify your profile so it is fully under your
                    control and ready to be optimized.
                  </p>
                </div>
              </li>
              <li>
                <span className="ledger-number"></span>
                <div>
                  <h3>Review Strategy</h3>
                  <p>
                    We set up a simple way for you to ask happy clients for
                    reviews and we respond to new reviews as they arrive.
                  </p>
                </div>
              </li>
              <li>
                <span className="ledger-number"></span>
                <div>
                  <h3>Ongoing Updates</h3>
                  <p>
                    We post fresh photos and short updates on a regular schedule
                    so your profile stays active.
                  </p>
                </div>
              </li>
              <li>
                <span className="ledger-number"></span>
                <div>
                  <h3>Monthly Reporting</h3>
                  <p>
                    We send a simple report each month showing what changed and
                    how your profile is performing.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section className="servicePage-section faq-section">
          <div className="section-inner">
            <h2 className="section-title">Before You Book Your Call</h2>

            <details className="faq-item">
              <summary>
                How fast will my Google Business Profile improve?
              </summary>
              <p>
                Most firms see their profile fill out and start collecting new
                reviews within the first month. Ranking changes can take longer
                since Google looks at activity over time.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I have to sign a long contract?</summary>
              <p>
                No. We work month to month. You stay because the results are
                working, not because a contract locks you in.
              </p>
            </details>

            <details className="faq-item">
              <summary>What if I already have reviews on my profile?</summary>
              <p>
                That is a great start. We build on what you already have and
                keep new reviews coming in on a steady basis.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I need to respond to reviews myself?</summary>
              <p>
                No. We handle the review requests, the responses, and the
                ongoing updates. You get a simple report showing what changed.
              </p>
            </details>
          </div>
        </section>

        <section className="servicePage-section closer-section">
          <div className="section-inner closer-inner">
            <img
              src={phone}
              alt="Calendar with a call icon and the text free 20 minute call"
              className="inline-image closer-image"
            />

            <h2 className="section-title">
              Ready To See What Is Missing From Your Profile?
            </h2>
            <p className="body-copy">
              Book a free 20 minute call. We will look at your current Google
              Business Profile and show you exactly what is holding it back.
            </p>
            <Link to="/contact" className="cta-button cta-button-large">
              Book A Free Call
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default GBPManagement;
